import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
  Bell,
  MessageCircle,
  FolderOpen,
  CheckCircle,
  Eye,
  TrendingUp,
  ChevronRight,
  X,
  MessageSquare,
  UserPlus,
  AlertTriangle,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useProfileStore } from '@/lib/state/profile-store';
import { useIsAdmin } from '@/lib/auth/role';
import { useAuth } from '@/lib/supabase';
import { useRealtimeNotifications } from '@/lib/supabase/realtime';
import { RealtimeNotification } from '@/lib/supabase';
import { mockAdminNotifications, type AdminNotification } from '@/lib/admin/mock-data';
import { mode } from '@/lib/config/app-mode';
import AnimatedPressable from '@/components/AnimatedPressable';
import { APP_LOCALE } from '@/lib/format';

// Mock notifications for demo mode (non-admin)
const mockUserNotifications: RealtimeNotification[] = [
  {
    id: '1',
    type: 'message',
    title: 'New message from Sarah Chen',
    body: 'I have a few questions about your Series A projections...',
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    read: false,
    data: { conversationId: 'conv-1' },
  },
  {
    id: '2',
    type: 'data_room_request',
    title: 'Data room access requested',
    body: 'Michael Roberts from Sequoia Capital has requested access.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    read: false,
    data: { investorId: 'inv-2', investorName: 'Michael Roberts' },
  },
  {
    id: '3',
    type: 'data_room_approved',
    title: 'Data room access granted',
    body: 'TechVenture AI has approved your data room access.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    read: true,
    data: { startupId: 'startup-1', startupName: 'TechVenture AI' },
  },
];

// Icon mapping for notification types (non-admin)
const getUserNotificationIcon = (type: string, colors: ReturnType<typeof useThemeColors>) => {
  const icons: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
    message: { icon: MessageCircle, color: colors.primary[600], bg: colors.primary[50] },
    data_room_request: { icon: FolderOpen, color: colors.traffic.amber, bg: colors.traffic.amberLight },
    data_room_approved: { icon: CheckCircle, color: colors.traffic.green, bg: colors.traffic.greenLight },
    profile_view: { icon: Eye, color: colors.info, bg: colors.primary[50] },
    score_update: { icon: TrendingUp, color: colors.success, bg: colors.traffic.greenLight },
  };
  return icons[type] || icons.message;
};

// Icon mapping for admin notification types
const getAdminNotificationIcon = (type: string, colors: ReturnType<typeof useThemeColors>) => {
  const icons: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
    new_feedback: { icon: MessageSquare, color: colors.traffic.amber, bg: colors.traffic.amberLight },
    new_signup: { icon: UserPlus, color: colors.primary[600], bg: colors.primary[50] },
    onboarding_complete: { icon: CheckCircle, color: colors.traffic.green, bg: colors.traffic.greenLight },
    critical_bug: { icon: AlertTriangle, color: colors.traffic.red, bg: colors.traffic.redLight },
  };
  return icons[type] || icons.new_feedback;
};

// Time formatting
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(APP_LOCALE);
}

interface NotificationDropdownProps {
  visible: boolean;
  onClose: () => void;
  anchorPosition?: { top: number; right: number };
}

export default function NotificationDropdown({
  visible,
  onClose,
  anchorPosition = { top: 60, right: 120 },
}: NotificationDropdownProps) {
  const router = useRouter();
  const colors = useThemeColors();
  const { width: viewportWidth } = useWindowDimensions();
  const userRole = useProfileStore((s) => s.userRole);

  /**
   * This panel renders only on web (`if (isWeb)` below) — but web includes a
   * PHONE BROWSER. At a fixed `width: 360` anchored `right: 120` it spanned
   * 480pt against a 375–393pt viewport, so the right third fell off-screen and
   * took the "mark all read" control with it.
   *
   * Clamp both: the panel never exceeds the viewport minus a gutter, and the
   * right offset never pushes it past the left edge. On a desktop viewport both
   * clamps are inert and the original 360/120 geometry is unchanged.
   */
  const GUTTER = 12;
  const panelWidth = Math.min(360, Math.max(240, viewportWidth - GUTTER * 2));
  const panelRight = Math.min(
    anchorPosition.right,
    Math.max(GUTTER, viewportWidth - panelWidth - GUTTER)
  );

  // REAL notifications, from the same shared cache the bell and /notifications
  // read. This component previously held its own useState seeded with
  // `mode.showSampleData ? mockUserNotifications : []`, so on web it showed a
  // populated dropdown in demo and an empty one for every real user — the
  // notification system's own UI was mock-only.
  const { user: authUser } = useAuth();

  // AUTHORITY from the server, not the persisted store. That store is a cache
  // maintained by index.tsx's effect, so landing directly on an admin route
  // leaves it holding the PERSONA ('investor' — admin is not a persona) and
  // this component would silently drop admin notifications. `userRole` above
  // stays for PERSONA routing (startup vs investor), which it is correct for.
  const { isAdmin } = useIsAdmin(authUser?.id);
  const {
    notifications: userNotifs,
    unreadCount: userUnread,
    markAsRead,
    markAllAsRead,
  } = useRealtimeNotifications(authUser?.id);

  // Admin notifications have no table yet, so they stay sample-only rather than
  // pretending. When an admin feed exists this becomes the same shape as above.
  const [adminNotifs, setAdminNotifs] = useState(mode.showSampleData ? mockAdminNotifications : []);

  const notifications = isAdmin ? adminNotifs : userNotifs;
  const unreadCount = isAdmin
    ? adminNotifs.filter((n) => !n.read).length
    : userUnread;

  const handleMarkRead = (id: string) => {
    if (isAdmin) {
      setAdminNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } else {
      markAsRead(id);
    }
  };

  const handleMarkAllRead = () => {
    if (isAdmin) {
      setAdminNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } else {
      markAllAsRead();
    }
  };

  const handleNotificationPress = (notification: RealtimeNotification | AdminNotification) => {
    if ('read' in notification && !notification.read) {
      handleMarkRead(notification.id);
    }
    onClose();

    if (isAdmin) {
      const adminNotif = notification as AdminNotification;
      switch (adminNotif.type) {
        case 'new_feedback':
        case 'critical_bug':
          router.push(`/(admin)/feedback-detail?id=${adminNotif.data.feedbackId}`);
          break;
        case 'new_signup':
        case 'onboarding_complete':
          router.push(`/(admin)/user-detail?id=${adminNotif.data.userId}`);
          break;
      }
    } else {
      const userNotif = notification as RealtimeNotification;

      // Same payload-first rule as the notifications screen: a notification
      // that names a startup goes to it regardless of `type`. Deck-analysis
      // rows are `type: 'system'` and matched no case here at all.
      const startupId = userNotif.data?.startupId;
      if (typeof startupId === 'string' && startupId.length > 0) {
        router.push(`/startup-detail?id=${startupId}`);
        return;
      }

      switch (userNotif.type) {
        case 'message':
          router.push(userRole === 'startup' ? '/(startup)/startup-messages' : '/(investor)/investor-messages');
          break;
        case 'data_room_request':
          router.push('/(startup)/dataroom');
          break;
        case 'data_room_approved':
          router.push('/(investor)/duediligence');
          break;
        case 'profile_view':
          if (userRole === 'startup') router.push('/(startup)/company');
          break;
        case 'score_update':
          router.push('/(startup)/score');
          break;
      }
    }
  };

  const handleViewAll = () => {
    onClose();
    if (isAdmin) {
      router.push('/(admin)/admin-dashboard');
    } else {
      router.push('/notifications');
    }
  };

  if (!visible) return null;

  // Web: dropdown panel — absolute positioned (no Modal to avoid click-through bug)
  if (isWeb) {
    return (
      <>
        {/* Backdrop — covers full parent */}
        <Pressable
          onPress={onClose}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
        {/* Dropdown */}
        <Animated.View
          entering={FadeIn.duration(150)}
          style={{
            position: 'absolute',
            top: anchorPosition.top,
            right: panelRight,
            width: panelWidth,
            maxHeight: 420,
            backgroundColor: colors.surface.primary,
            borderRadius: 16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 24,
            borderWidth: 1,
            borderColor: colors.border.light,
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
              {/* Header */}
              <View
                className="flex-row items-center justify-between px-4 py-3"
                style={{ borderBottomWidth: 1, borderColor: colors.border.light }}
              >
                <View className="flex-row items-center gap-2">
                  <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                    Notifications
                  </Text>
                  {unreadCount > 0 && (
                    <View
                      className="px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: colors.traffic.red }}
                    >
                      <Text className="text-xs font-bold text-white">{unreadCount}</Text>
                    </View>
                  )}
                </View>
                <View className="flex-row items-center gap-2">
                  {unreadCount > 0 && (
                    <Pressable
                      onPress={handleMarkAllRead}
                      className="px-2 py-1 rounded active:opacity-70"
                    >
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                        Mark all read
                      </Text>
                    </Pressable>
                  )}
                  <Pressable onPress={onClose} className="w-7 h-7 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
                    <X size={14} color={colors.text.tertiary} />
                  </Pressable>
                </View>
              </View>

              {/* Notifications List */}
              <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={false}>
                {notifications.length === 0 ? (
                  <View className="py-8 items-center">
                    <Bell size={28} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm mt-2">
                      No notifications
                    </Text>
                  </View>
                ) : (
                  notifications.slice(0, 5).map((notification, index) => {
                    const iconConfig = isAdmin
                      ? getAdminNotificationIcon((notification as AdminNotification).type, colors)
                      : getUserNotificationIcon((notification as RealtimeNotification).type, colors);
                    const NotifIcon = iconConfig.icon;
                    const isRead = 'read' in notification ? notification.read : true;
                    const createdAt = isAdmin
                      ? (notification as AdminNotification).createdAt
                      : (notification as RealtimeNotification).createdAt;

                    return (
                      <AnimatedPressable
                        key={notification.id}
                        onPress={() => handleNotificationPress(notification)}
                        className="flex-row items-start gap-3 px-4 py-3"
                        style={{
                          backgroundColor: isRead
                            ? colors.surface.primary
                            : colors.primary[50],
                          borderBottomWidth: index < notifications.length - 1 ? 1 : 0,
                          borderColor: colors.border.light,
                        }}
                      >
                        <View
                          className="w-9 h-9 rounded-full items-center justify-center"
                          style={{ backgroundColor: iconConfig.bg }}
                        >
                          <NotifIcon size={16} color={iconConfig.color} />
                        </View>
                        <View className="flex-1">
                          <View className="flex-row items-center justify-between">
                            <Text
                              style={{ color: colors.text.primary }}
                              className={`text-sm flex-1 ${isRead ? '' : 'font-semibold'}`}
                              numberOfLines={1}
                            >
                              {notification.title}
                            </Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs ml-2">
                              {formatTimeAgo(createdAt)}
                            </Text>
                          </View>
                          <Text
                            style={{ color: colors.text.secondary }}
                            className="text-xs mt-0.5"
                            numberOfLines={1}
                          >
                            {notification.body}
                          </Text>
                        </View>
                        {!isRead && (
                          <View
                            className="w-2 h-2 rounded-full mt-1.5"
                            style={{ backgroundColor: colors.primary[600] }}
                          />
                        )}
                      </AnimatedPressable>
                    );
                  })
                )}
              </ScrollView>

              {/* Footer */}
              <AnimatedPressable
                onPress={handleViewAll}
                className="flex-row items-center justify-center gap-1 py-3"
                style={{ borderTopWidth: 1, borderColor: colors.border.light }}
              >
                <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                  {isAdmin ? 'Go to Dashboard' : 'View all notifications'}
                </Text>
                <ChevronRight size={14} color={colors.primary[600]} />
              </AnimatedPressable>
            </Pressable>
          </Animated.View>
        </>
    );
  }

  // Mobile: navigate to full page instead
  return null;
}
